import { useCallback, useLayoutEffect, useRef, type RefObject } from 'react';
import type { CommentState } from './commentStore';

/** How close to the bottom still counts as parked there. */
const BOTTOM_THRESHOLD = 8;

export type StreamScroll = {
  containerRef: RefObject<HTMLDivElement>;
  /** Remember whether the reader is parked at the bottom. */
  onScroll: () => void;
  /** Capture the pre-prepend height. Call immediately before requesting an older page. */
  captureBeforePrepend: () => void;
  /** Jump to the newest comment. For the reader's own submit. */
  scrollToEnd: () => void;
};

/**
 * Every scroll position the stream takes on its reader's behalf. The three
 * behaviours are independent and each owns its own bookkeeping, so none of them
 * has to reason about the others having already run.
 */
export function useStreamScroll(state: CommentState): StreamScroll {
  const { anchor, loadedTo, loading } = state;
  const containerRef = useRef<HTMLDivElement>(null);
  const atBottomRef = useRef(true);
  const prependHeightRef = useRef<number | null>(null);
  const positionedRef = useRef(false);
  const previousLoadedToRef = useRef<number | null>(null);

  const onScroll = useCallback(() => {
    const container = containerRef.current;
    if (container) {
      atBottomRef.current = container.scrollHeight - container.scrollTop - container.clientHeight <= BOTTOM_THRESHOLD;
    }
  }, []);

  const captureBeforePrepend = useCallback(() => {
    const container = containerRef.current;
    if (container) {
      prependHeightRef.current = container.scrollHeight;
    }
  }, []);

  const scrollToEnd = useCallback(() => {
    window.requestAnimationFrame(() => {
      const container = containerRef.current;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    });
  }, []);

  // Hold the reader's place across a prepend. Every older page settles by
  // clearing this flag in the same commit that grows the list, and a failed one
  // grows nothing, so the delta is zero and this only clears the capture.
  useLayoutEffect(() => {
    const container = containerRef.current;
    const captured = prependHeightRef.current;
    if (!container || captured === null || loading.older) {
      return;
    }
    container.scrollTop += container.scrollHeight - captured;
    prependHeightRef.current = null;
  }, [loading.older]);

  // Position a freshly built window once: on its anchor, or at the tail.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (loadedTo === null) {
      positionedRef.current = false;
      return;
    }
    if (!container || positionedRef.current) {
      return;
    }
    positionedRef.current = true;

    if (anchor === null) {
      container.scrollTop = container.scrollHeight;
      return;
    }
    container.querySelector<HTMLElement>(`[data-comment-id="${anchor}"]`)?.scrollIntoView({ block: 'center' });
  }, [anchor, loadedTo]);

  // Follow the tail as it grows, but only for a reader who is already there.
  useLayoutEffect(() => {
    const container = containerRef.current;
    const previous = previousLoadedToRef.current;
    previousLoadedToRef.current = loadedTo;

    if (container && previous !== null && loadedTo !== null && loadedTo > previous && atBottomRef.current) {
      container.scrollTop = container.scrollHeight;
    }
  }, [loadedTo]);

  return { containerRef, onScroll, captureBeforePrepend, scrollToEnd };
}
